import { supabase } from '@/lib/supabase/supabaseClient';
import { getQuestionsBySurveyIdProps } from '.';
import { populateQuestionsWithHasResponse } from '@/lib/utils/query/question/populateQuestionWithHasResponse';
import { getSurveyById } from '@/features/survey/hooks/useQuery/useGetSurveyById';
import { evaluateQuestionsConditionals } from '@/lib/utils/query/question/evaluateQuestionsConditionals';
import { getUserData } from '@/features/user/hooks/useQuery/useUserData';
import { sortQuestionsByDependencies } from './utils/sortQuestionsByDependencies';
import { groupQuestionsByDependencies } from './utils/groupQuestionsByDependencies';
import { populateUsersDataByTheirIds } from '@/lib/utils/query/user/populateUsersDataByTheirIds';

export const getQuestionsBySurveyId = async ({
  surveyId,
  sortByCreatedAtAscending,
  showAll = false,
}: getQuestionsBySurveyIdProps) => {
  console.log(surveyId);
  const surveyData = await getSurveyById(surveyId);
  const userData = await getUserData();
  const orgRbac = await supabase
    .from('role_based_organization_access_control')
    .select('*')
    .throwOnError()
    .then((res) => res.data || []);
  const projectRbac = await supabase
    .from('role_based_project_access_control')
    .select('*')
    .throwOnError()
    .then((res) => res.data || []);

  if (!surveyData) {
    throw new Error('Survey not found');
  }
  if (showAll && !userData) {
    throw new Error("User not found, can't evaluate conditionals");
  }
  const orgMembersRoles = await supabase
    .from('user_organization_role')
    .select('*')
    .eq('organization_id', surveyData.project.organisation_id)
    .throwOnError()
    .then((res) => res.data);
  const projectMembersRoles = await supabase
    .from('user_project_role')
    .select('*')
    .eq('project_id', surveyData.project_id)
    .throwOnError()
    .then((res) => res.data);
  return await supabase
    .from('question')
    .select(
      `
                *,
                user(*),
                response(
                    *,
                    user(*)
                ),
                survey(*,project(*,project_members(*)))
            `
    )
    .eq('survey_id', surveyId)
    // .eq('archived', isArchived)
    .order('created_at', { ascending: sortByCreatedAtAscending })
    .order('created_at', { referencedTable: 'response', ascending: true })
    .throwOnError()
    .then((res) => res.data)
    .then(populateQuestionsWithHasResponse)
    .then((res) =>
      evaluateQuestionsConditionals(
        res,
        surveyData?.project?.project_members?.map((member) => member.user_id),
        showAll ? userData?.id : undefined
      )
    )
    .then((res) => {
      const questionsWithPopulatedUserData = res.map((question) => ({
        ...question,
        condition_met_for_users: populateUsersDataByTheirIds({
          usersWithIdOnly: question.condition_met_for_users || [],
          users:
            surveyData?.project?.project_members?.map(
              (member) => member.user
            ) || [],
          userOrganizationRole: orgMembersRoles || [],
          organizationRoleRbac: orgRbac || [],
          userProjectRole: projectMembersRoles || [],
          projectRoleRbac: projectRbac || [],
        }),
      }));
      return {
        sorted: sortQuestionsByDependencies(questionsWithPopulatedUserData),
        grouped: groupQuestionsByDependencies(questionsWithPopulatedUserData),
        questions: questionsWithPopulatedUserData,
      };
    });
};
